import React from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Store, Inbox, Settings, LogOut, ExternalLink, Users, Palette } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cercle-local-bhealth/artifacts/csfd9ojj_logofinal-logo-full-color-rgb-900px-w-72ppi.png";

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-muted"><div className="w-10 h-10 rounded-full border-2 border-navy-900 border-t-transparent animate-spin" /></div>;
  }
  if (!user || user === false) {
    nav("/admin/login", { replace: true });
    return null;
  }

  const allLinks = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/partenaires", label: "Partenaires", icon: Store },
    { to: "/admin/soumissions", label: "Soumissions", icon: Inbox },
    { to: "/admin/parametres", label: "Paramètres", icon: Settings, roles: ["super_admin", "marketing"] },
    { to: "/admin/configuration", label: "Brand CMS", icon: Palette, roles: ["super_admin"] },
    { to: "/admin/utilisateurs", label: "Utilisateurs", icon: Users, roles: ["super_admin"] },
  ];
  const links = allLinks.filter((l) => !l.roles || l.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-muted flex">
      <aside className="w-64 bg-navy-900 text-white hidden md:flex flex-col" data-testid="admin-sidebar">
        <div className="p-6">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="B.health®" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-heading font-extrabold uppercase tracking-wider text-sm">Local Benefits</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-semibold">Admin</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`admin-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition ${
                    isActive ? "bg-lime-500 text-navy-900" : "text-white/80 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <Icon className="w-4 h-4" /> {l.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-sm text-white/70 mb-1 truncate">{user.email}</div>
          <div className="text-[11px] uppercase tracking-widest text-lime-400 font-semibold mb-3">{user.role?.replace("_", " ")}</div>
          <a href="/" target="_blank" rel="noreferrer" className="text-xs text-white/60 hover:text-lime-400 flex items-center gap-1.5 mb-2"><ExternalLink className="w-3 h-3" /> Voir le site</a>
          <button onClick={() => { logout(); nav("/admin/login"); }} className="flex items-center gap-2 text-sm text-white/70 hover:text-lime-400" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-auto">
        <div className="md:hidden bg-navy-900 text-white p-4 flex items-center gap-3">
          <img src={LOGO_URL} alt="B.health®" className="h-7 w-7 object-contain" />
          <span className="font-heading font-bold uppercase tracking-wider text-sm">Local Benefits Admin</span>
          <button onClick={logout} className="ml-auto text-white/70"><LogOut className="w-4 h-4" /></button>
        </div>
        <div className="md:hidden flex overflow-x-auto gap-1 p-2 bg-white border-b">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `shrink-0 px-4 py-2 rounded-full text-xs font-semibold ${isActive ? "bg-navy-900 text-white" : "text-navy-900/70"}`
              }
            >{l.label}</NavLink>
          ))}
        </div>
        <div className="p-6 md:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
